import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import accountingService, { BankAccountDto, CashboxDto } from '../../../services/accountingService';
import {
  CourierSettlementDay,
  CourierSettlementRow,
  courierModuleService,
  formatMoney,
  suggestMismatch,
} from './courierModuleService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Açılışta seçili şube (paket ekranının şubesi). */
  branchId?: string | null;
  /** Kapatma sonrası panel yenilensin diye. */
  onChanged?: () => void;
  /** Şube yöneticisi: şube değiştirilemez. */
  lockBranch?: boolean;
}

interface Entry {
  counted: string;
  slip: string;
  payFee: boolean;
}

const todayLocal = (): string => {
  const d = new Date(Date.now() + 3 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
};

const num = (v: string): number | null => {
  const s = v.replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

const round = (v: number) => Math.round(v * 100) / 100;

/** Gün sonu kurye hesaplaşması: sistemdeki nakit / sayılan nakit / POS; "Teslim alındı" ile kasaya aktarım. */
const CourierSettlementDialog: React.FC<Props> = ({ open, onClose, branchId, onChanged, lockBranch }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [branch, setBranch] = useState<string>('');
  const [date, setDate] = useState<string>(todayLocal());
  const [day, setDay] = useState<CourierSettlementDay | null>(null);
  const [entries, setEntries] = useState<Record<number, Entry>>({});
  const [selected, setSelected] = useState<number[]>([]);
  const [dismissed, setDismissed] = useState<number[]>([]);
  const [cashboxes, setCashboxes] = useState<CashboxDto[]>([]);
  const [banks, setBanks] = useState<BankAccountDto[]>([]);
  const [targetId, setTargetId] = useState<number | ''>('');
  const [posBankId, setPosBankId] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | 'bulk' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBranch(branchId || (branches?.[0] as any)?.id || '');
    setDate(todayLocal());
    setInfo(null);
    setError(null);
    accountingService.getCashboxes()
      .then((boxes) => setCashboxes(boxes.filter((c) => !c.IsDeleted && !(c.Name || '').startsWith('Kurye · '))))
      .catch(() => setCashboxes([]));
    accountingService.getBankAccounts()
      .then((list) => setBanks(list.filter((b) => !(b.isDeleted ?? b.IsDeleted))))
      .catch(() => setBanks([]));
  }, [open, branchId, branches]);

  const load = useCallback(async () => {
    if (!open || !branch) return;
    setLoading(true);
    setError(null);
    try {
      const d = await courierModuleService.settlementDay(branch, date);
      setDay(d);
      setTargetId(d.settlementCashboxId ?? '');
      setEntries((prev) => {
        const next: Record<number, Entry> = {};
        d.rows.forEach((r) => {
          next[r.courierId] = prev[r.courierId] ?? { counted: String(r.cashSystem), slip: '', payFee: r.paidFromCash };
        });
        return next;
      });
      setSelected((prev) => prev.filter((id) => d.rows.some((r) => r.courierId === id)));
    } catch (e: any) {
      setError(e?.message || t('courierModule.settlement.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [open, branch, date, t]);

  useEffect(() => { void load(); }, [load]);

  const entryOf = (r: CourierSettlementRow): Entry =>
    entries[r.courierId] ?? { counted: String(r.cashSystem), slip: '', payFee: r.paidFromCash };

  const setEntry = (id: number, patch: Partial<Entry>) =>
    setEntries((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { counted: '', slip: '', payFee: false }), ...patch } }));

  const calc = (r: CourierSettlementRow) => {
    const e = entryOf(r);
    const counted = num(e.counted);
    const slip = num(e.slip);
    const diff = counted == null ? null : round(counted - r.cashSystem);
    const fee = e.payFee && counted != null ? Math.min(r.feeTotal, counted) : 0;
    const handover = counted == null ? null : round(counted - fee);
    return { counted, slip, diff, fee, handover, payFee: e.payFee };
  };

  const close = async (rows: CourierSettlementRow[]) => {
    setError(null);
    setInfo(null);
    const done: string[] = [];
    for (const r of rows) {
      const c = calc(r);
      if (c.counted == null) {
        setError(t('courierModule.settlement.countedRequired', { name: r.name }));
        break;
      }
      try {
        await courierModuleService.closeSettlement({
          courierId: r.courierId,
          branchId: branch,
          date,
          countedCash: c.counted,
          cardSlip: c.slip,
          payFeeFromCash: c.payFee,
          targetCashboxId: targetId === '' ? null : Number(targetId),
          note: note.trim() || null,
        });
        done.push(r.name || `#${r.courierId}`);
      } catch (e: any) {
        setError(`${r.name}: ${e?.message || t('courierModule.settlement.closeFailed')}`);
        break;
      }
    }
    if (done.length > 0) {
      setInfo(t('courierModule.settlement.closed', { names: done.join(', ') }));
      setSelected([]);
      onChanged?.();
    }
    await load();
  };

  const closeOne = async (r: CourierSettlementRow) => {
    setBusy(r.courierId);
    try { await close([r]); } finally { setBusy(null); }
  };

  const closeSelected = async () => {
    if (!day) return;
    setBusy('bulk');
    try { await close(day.rows.filter((r) => selected.includes(r.courierId))); } finally { setBusy(null); }
  };

  const correct = async (collectionId: number) => {
    setBusy(collectionId);
    setError(null);
    try {
      await courierModuleService.correctCollection(collectionId, {
        method: 'POS',
        bankAccountId: posBankId === '' ? null : Number(posBankId),
      });
      setInfo(t('courierModule.settlement.corrected'));
      onChanged?.();
      await load();
    } catch (e: any) {
      setError(e?.message || t('courierModule.settlement.correctFailed'));
    } finally {
      setBusy(null);
    }
  };

  const branchName = useMemo(
    () => (branches || []).find((b: any) => b.id === branch)?.name || '',
    [branches, branch],
  );

  const kindLabel = (r: CourierSettlementRow) => {
    const kind = t(`courierModule.kinds.${r.kind}`, { defaultValue: r.kind });
    if (r.kind === 'OWN') return kind;
    return `${kind} · ${r.usesApp ? t('courierModule.settlement.withApp') : t('courierModule.settlement.withoutApp')}`;
  };

  const feeNote = (r: CourierSettlementRow) => {
    if (r.kind === 'OWN' && !r.paidFromCash) return t('courierModule.settlement.feeWithSalary');
    if (r.feeExtras > 0) return t('courierModule.settlement.feeWithExtras');
    return null;
  };

  const rows = day?.rows ?? [];
  const noCourierCashbox = day != null && !day.courierCashboxEnabled;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography component="span" variant="h6" fontWeight={800}>{t('courierModule.settlement.title')}</Typography>
        <Chip size="small" label={`${date.split('-').reverse().join('.')}${branchName ? ` · ${branchName}` : ''}`} />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          disabled={selected.length === 0 || busy != null}
          onClick={closeSelected}
          startIcon={busy === 'bulk' ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('courierModule.settlement.closeSelected', { count: selected.length })}
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField select size="small" label={t('courierModule.settlement.branch')} value={branch}
              onChange={(e) => setBranch(e.target.value)} sx={{ minWidth: 220 }} disabled={lockBranch}>
              {(branches || []).map((b: any) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              {branch && !(branches || []).some((b: any) => b.id === branch) && <MenuItem value={branch}>{branchName || branch}</MenuItem>}
            </TextField>
            <TextField type="date" size="small" label={t('courierModule.settlement.date')} value={date}
              onChange={(e) => setDate(e.target.value || todayLocal())} InputLabelProps={{ shrink: true }} />
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {info && <Alert severity="success" onClose={() => setInfo(null)}>{info}</Alert>}
          {noCourierCashbox && <Alert severity="info">{t('courierModule.settlement.noCourierCashbox')}</Alert>}

          {loading && !day ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
          ) : rows.length === 0 ? (
            <Alert severity="success">{t('courierModule.settlement.empty')}</Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1050 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t('courierModule.settlement.cols.courier')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.packages')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.cashSystem')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.counted')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.difference')}</TableCell>
                    <TableCell>{t('courierModule.settlement.cols.pos')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.fee')}</TableCell>
                    <TableCell align="right">{t('courierModule.settlement.cols.handover')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const c = calc(r);
                    const e = entryOf(r);
                    const flagged = (c.diff ?? 0) !== 0 || (c.slip != null && round(c.slip - r.cardSystem) !== 0);
                    const blocked = r.blockingCount > 0 || r.hasPendingHandover;
                    return (
                      <TableRow key={r.courierId} sx={flagged ? { bgcolor: 'warning.50', '& td': { bgcolor: 'rgba(255,193,7,0.06)' } } : undefined}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            disabled={blocked}
                            checked={selected.includes(r.courierId)}
                            onChange={(ev) => setSelected((prev) => ev.target.checked
                              ? [...prev, r.courierId]
                              : prev.filter((id) => id !== r.courierId))}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={700} noWrap>{r.name}</Typography>
                          <Chip size="small" variant="outlined" label={kindLabel(r)} color={r.kind === 'OWN' ? 'info' : 'warning'} />
                        </TableCell>
                        <TableCell align="right">{r.packageCount}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>{formatMoney(r.cashSystem)}</Typography>
                          {r.intradayHandedOver > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('courierModule.handover.settlementLine', { amount: formatMoney(r.intradayHandedOver) })}
                            </Typography>
                          )}
                          {r.carriedIn !== 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              {t('courierModule.settlement.carriedIn', { amount: formatMoney(r.carriedIn) })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={e.counted}
                            onChange={(ev) => setEntry(r.courierId, { counted: ev.target.value })}
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                            InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={(c.diff ?? 0) < 0 ? 'error.main' : (c.diff ?? 0) > 0 ? 'success.main' : 'text.primary'}>
                            {c.diff == null ? '—' : c.diff === 0 ? '0' : formatMoney(c.diff)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" noWrap>{t('courierModule.settlement.posSystem', { amount: formatMoney(r.cardSystem) })}</Typography>
                            <TextField
                              size="small"
                              placeholder={t('courierModule.settlement.slip')}
                              value={e.slip}
                              onChange={(ev) => setEntry(r.courierId, { slip: ev.target.value })}
                              inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                              sx={{ width: 110 }}
                            />
                            {c.slip != null && round(c.slip - r.cardSystem) !== 0 && (
                              <Chip size="small" color="warning"
                                label={`${c.slip - r.cardSystem > 0 ? '+' : ''}${formatMoney(round(c.slip - r.cardSystem))}`} />
                            )}
                            {r.pavoSystem > 0 && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {t('courierModule.settlement.pavoAuto', { amount: formatMoney(r.pavoSystem) })}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>{formatMoney(r.feeTotal)}</Typography>
                          {feeNote(r) && <Typography variant="caption" color="text.secondary" display="block">{feeNote(r)}</Typography>}
                          {r.feeTotal > 0 && !(r.kind === 'OWN' && !r.paidFromCash) && (
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={<Checkbox size="small" checked={e.payFee}
                                onChange={(ev) => setEntry(r.courierId, { payFee: ev.target.checked })} />}
                              label={<Typography variant="caption">{t('courierModule.settlement.payFromCash')}</Typography>}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={800}>{c.handover == null ? '—' : formatMoney(c.handover)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={blocked ? t('courierModule.settlement.blocked', { count: r.blockingCount }) : ''}>
                            <span>
                              <Button
                                size="small"
                                variant={c.diff === 0 ? 'contained' : 'outlined'}
                                color={c.diff === 0 ? 'success' : 'primary'}
                                disabled={blocked || busy != null || c.counted == null}
                                onClick={() => closeOne(r)}
                                startIcon={busy === r.courierId ? <CircularProgress size={14} color="inherit" /> : undefined}
                              >
                                {c.diff === 0 ? t('courierModule.settlement.received') : t('courierModule.settlement.close')}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Uyarılar: faturalanmamış ödeme, eksik nakit önerisi, garanti farkı */}
          {rows.map((r) => {
            const c = calc(r);
            const notes: React.ReactNode[] = [];
            if (r.hasPendingHandover) {
              notes.push(
                <Alert key="h" severity="warning">
                  {t('courierModule.handover.settlementBlocked', { name: r.name })}
                </Alert>,
              );
            }
            if (r.blockingCount > 0) {
              notes.push(
                <Alert key="b" severity="error">
                  {t('courierModule.settlement.blockedLong', { name: r.name, count: r.blockingCount })}
                </Alert>,
              );
            }
            const suggestions = c.counted == null ? [] : suggestMismatch(r, c.counted, c.slip)
              .filter((s) => !dismissed.includes(s.id));
            suggestions.forEach((s) => {
              notes.push(
                <Alert key={`s${s.id}`} severity="warning"
                  action={(
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="inherit" variant="outlined" disabled={busy != null} onClick={() => correct(s.id)}
                        startIcon={busy === s.id ? <CircularProgress size={14} color="inherit" /> : undefined}>
                        {t('courierModule.settlement.correctToCard')}
                      </Button>
                      <Button size="small" color="inherit" onClick={() => setDismissed((p) => [...p, s.id])}>
                        {t('courierModule.settlement.keepDifference')}
                      </Button>
                    </Stack>
                  )}>
                  {t('courierModule.settlement.suggestion', {
                    name: r.name,
                    amount: formatMoney(s.cashAmount),
                    order: s.orderNumber || `#${s.orderRef}`,
                  })}
                </Alert>,
              );
            });
            if (r.feeExtras > 0) {
              notes.push(
                <Alert key="x" severity="info">
                  {t('courierModule.settlement.extrasInfo', {
                    name: r.name,
                    packages: formatMoney(r.feePackages),
                    extras: formatMoney(r.feeExtras),
                  })}
                </Alert>,
              );
            }
            return notes.length > 0 ? <Stack key={r.courierId} spacing={1}>{notes}</Stack> : null;
          })}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField select size="small" label={t('courierModule.settlement.targetCashbox')} value={targetId}
              onChange={(e) => setTargetId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 220 }}>
              <MenuItem value="">{t('courierModule.settlement.targetFromSettings')}</MenuItem>
              {cashboxes.map((c) => <MenuItem key={c.Id} value={c.Id}>{c.Name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label={t('courierModule.settlement.posAccount')} value={posBankId}
              onChange={(e) => setPosBankId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 220 }}>
              <MenuItem value="">{t('courierModule.settlement.posFromScreen')}</MenuItem>
              {banks.map((b) => {
                const id = b.id ?? b.Id;
                return <MenuItem key={id} value={id}>{b.bankName ?? b.BankName}</MenuItem>;
              })}
            </TextField>
            <TextField size="small" label={t('courierModule.settlement.note')} value={note}
              onChange={(e) => setNote(e.target.value)} sx={{ flex: 1 }} inputProps={{ maxLength: 500 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">{t('courierModule.settlement.shortageHint')}</Typography>

          {day && day.closed.length > 0 && (
            <Box>
              <Typography fontWeight={800} sx={{ mb: 0.5 }}>{t('courierModule.settlement.closedTitle')}</Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('courierModule.settlement.cols.courier')}</TableCell>
                      <TableCell align="right">{t('courierModule.settlement.cols.counted')}</TableCell>
                      <TableCell align="right">{t('courierModule.settlement.cols.difference')}</TableCell>
                      <TableCell align="right">{t('courierModule.settlement.cols.feeFromCash')}</TableCell>
                      <TableCell align="right">{t('courierModule.settlement.cols.handover')}</TableCell>
                      <TableCell>{t('courierModule.settlement.cols.cashbox')}</TableCell>
                      <TableCell>{t('courierModule.settlement.cols.closedBy')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {day.closed.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          {s.courierName}
                          {s.status !== 'closed' && (
                            <Tooltip title={s.error || ''}>
                              <Chip size="small" color="error" sx={{ ml: 1 }} label={t(`courierModule.settlement.status.${s.status}`)} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatMoney(s.cashCounted)}</TableCell>
                        <TableCell align="right" sx={{ color: s.cashDifference < 0 ? 'error.main' : undefined }}>
                          {s.cashDifference === 0 ? '0' : formatMoney(s.cashDifference)}
                        </TableCell>
                        <TableCell align="right">{s.feeFromCash > 0 ? formatMoney(s.feeFromCash) : '—'}</TableCell>
                        <TableCell align="right">{formatMoney(s.handedOver)}</TableCell>
                        <TableCell>{s.targetCashboxName || '—'}</TableCell>
                        <TableCell>
                          {s.createdBy || '—'}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            {new Date(s.createdAt.endsWith('Z') ? s.createdAt : `${s.createdAt}Z`)
                              .toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy != null}>{t('courierModule.actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourierSettlementDialog;
